import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    customColors: {
      themePrimary: string;
      themeSecondary: string;
      themeTertiary: string;
      themeQuaternary: string;
      themeDanger: string;
      themeSuccess: string;
      themeEmojiYellow: string;
      themeWarningLight: string;
      themeWarningMain: string;
      themeWarningDark: string;
      themeNeutralPrimaryColor: string;
      themeNeutralSecondaryColor: string;
      themeNeutralTertiaryColor: string;
      /** Account campaigns: spreadsheet stripe, toggles, chips (deeper blue than `themePrimary`). */
      spreadsheetAccountAccent: string;
      /** Leads view accent (account campaigns only); neutral gray, not a brand color. */
      spreadsheetLeadAccent: string;
      /** Opportunity campaigns spreadsheet stripe; distinct from account and lead accents. */
      spreadsheetOpportunityAccent: string;
      /** Light purple header tint for prioritization columns; same hex as `campaignChromeOpportunityBg` (distinct from primary blue `#E8F4FC`). */
      spreadsheetPrioritizationHeaderBg: string;
      /** Campaign page header bar tint (accounts view); very light blue, distinct from leads/opportunities. */
      campaignChromeAccountBg: string;
      /** Campaign page header bar tint (leads); neutral white-gray (matches surrounding chrome). */
      campaignChromeLeadBg: string;
      /** Campaign page header bar tint (opportunity campaigns). */
      campaignChromeOpportunityBg: string;
      /** Campaign name on tinted campaign header. */
      campaignHeaderTitleColor: string;
    };
  }
  interface ThemeOptions {
    customColors: {
      themePrimary: string;
      themeSecondary: string;
      themeTertiary: string;
      themeQuaternary: string;
      themeDanger: string;
      themeSuccess: string;
      themeEmojiYellow: string;
      themeWarningLight: string;
      themeWarningMain: string;
      themeWarningDark: string;
      themeNeutralPrimaryColor: string;
      themeNeutralSecondaryColor: string;
      themeNeutralTertiaryColor: string;
      spreadsheetAccountAccent: string;
      spreadsheetLeadAccent: string;
      spreadsheetOpportunityAccent: string;
      spreadsheetPrioritizationHeaderBg: string;
      campaignChromeAccountBg: string;
      campaignChromeLeadBg: string;
      campaignChromeOpportunityBg: string;
      campaignHeaderTitleColor: string;
    };
  }
}

/** Brand light blue; primary actions and global link affordance. */
const THEME_PRIMARY_BLUE = '#49BAFD';

const dokaiTheme = createTheme({
  customColors: {
    themePrimary: THEME_PRIMARY_BLUE,
    themeSecondary: '#FFFFFF',
    themeTertiary: '#F1F3F4',
    themeQuaternary: '#E5E7E9',
    themeDanger: '#DC3545',
    themeSuccess: '#28A745',
    themeEmojiYellow: '#FBDC34',
    themeWarningLight: '#FFF4E5',
    themeWarningMain: '#ED6C02',
    themeWarningDark: '#663C00',
    themeNeutralPrimaryColor: '#000000',
    themeNeutralSecondaryColor: '#AAAAAA',
    themeNeutralTertiaryColor: '#FCFCFC',
    spreadsheetAccountAccent: '#0369A1',
    spreadsheetLeadAccent: '#6B7280',
    spreadsheetOpportunityAccent: '#6B4FA3',
    spreadsheetPrioritizationHeaderBg: '#E4DCF2',
    campaignChromeAccountBg: '#E8F4FC',
    campaignChromeLeadBg: '#F1F3F4',
    campaignChromeOpportunityBg: '#E4DCF2',
    campaignHeaderTitleColor: '#0F172A'
  }
});

/**
 * Lightens a hex color by a given percentage.
 * @param hex - 6-digit hex code, e.g. "#49BAFD"
 * @param percent - Value between 0 (no change) and 1 (white)
 */
const lightenHexColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(
    255,
    Math.round(((num >> 16) & 0xff) + (255 - ((num >> 16) & 0xff)) * percent)
  );
  const g = Math.min(
    255,
    Math.round(((num >> 8) & 0xff) + (255 - ((num >> 8) & 0xff)) * percent)
  );
  const b = Math.min(
    255,
    Math.round((num & 0xff) + (255 - (num & 0xff)) * percent)
  );
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

/**
 * Generates a color palette based on theme colors and number of steps.
 * @param baseColors - An array of base hex colors from your theme
 * @param count - Total number of desired colors (e.g., for workflows)
 * @returns An array of hex strings
 */
const generateThemePalette = (
  baseColors: string[],
  count: number
): string[] => {
  const palette: string[] = [];
  const lightenSteps = [0.15, 0.25, 0.35, 0.45, 0.55];

  for (let i = 0; i < count; i++) {
    const base = baseColors[i % baseColors.length];
    const lighten = lightenSteps[i % lightenSteps.length];
    palette.push(lightenHexColor(base, lighten));
  }

  return palette;
};

export { dokaiTheme, generateThemePalette };
export {
  resolveCampaignHeaderBackground,
  type CampaignHeaderChromePalette
} from './campaignChrome';
